import React from 'react';
import { Wrench, Settings, ClipboardCheck, AlertCircle, Hammer, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const MaintenanceServices = () => {
  const { language } = useLanguage();

  const services = [
    {
      icon: Settings,
      title: { fr: 'Maintenance Préventive', en: 'Preventive Maintenance' },
      description: { fr: 'Évitez les pannes avec un entretien régulier', en: 'Avoid breakdowns with regular maintenance' },
      image: 'https://images.unsplash.com/photo-1606485940233-76eeff49360c',
      features: [
        { fr: 'Inspections programmées', en: 'Scheduled inspections' },
        { fr: 'Vérifications de routine', en: 'Routine checks' },
        { fr: 'Lubrification et ajustements', en: 'Lubrication and adjustments' },
        { fr: 'Remplacement pièces d’usure', en: 'Wear parts replacement' },
        { fr: 'Rapports détaillés', en: 'Detailed reports' }
      ]
    },
    {
      icon: AlertCircle,
      title: { fr: 'Réparations d’Urgence', en: 'Emergency Repairs' },
      description: { fr: 'Intervention rapide 24/7', en: 'Quick 24/7 intervention' },
      image: 'https://images.unsplash.com/photo-1595856898575-9d187bd32fd6',
      features: [
        { fr: 'Disponibilité 24h/24, 7j/7', en: '24/7 availability' },
        { fr: 'Temps de réponse < 4h', en: 'Response time < 4h' },
        { fr: 'Diagnostic rapide', en: 'Quick diagnosis' },
        { fr: 'Réparation sur site', en: 'On-site repair' },
        { fr: 'Pièces de rechange disponibles', en: 'Spare parts available' }
      ]
    },
    {
      icon: Hammer,
      title: { fr: 'Outillage Professionnel', en: 'Professional Tools' },
      description: { fr: 'Outils et équipements de maintenance', en: 'Maintenance tools and equipment' },
      image: 'https://images.unsplash.com/photo-1744302570694-3f8949445c2b',
      features: [
        { fr: 'Outils manuels professionnels', en: 'Professional hand tools' },
        { fr: 'Outils électriques', en: 'Power tools' },
        { fr: 'Équipements de diagnostic', en: 'Diagnostic equipment' },
        { fr: 'Consommables de maintenance', en: 'Maintenance consumables' },
        { fr: 'Chariots d’intervention', en: 'Service carts' }
      ]
    },
    {
      icon: ClipboardCheck,
      title: { fr: 'Inspections Régulières', en: 'Regular Inspections' },
      description: { fr: 'Contrôles de conformité et sécurité', en: 'Compliance and safety checks' },
      image: 'https://images.unsplash.com/photo-1513828583688-c52646db42da',
      features: [
        { fr: 'Inspections de sécurité', en: 'Safety inspections' },
        { fr: 'Contrôles réglementaires', en: 'Regulatory checks' },
        { fr: 'Tests de performance', en: 'Performance tests' },
        { fr: 'Certification de conformité', en: 'Compliance certification' },
        { fr: 'Documentation complète', en: 'Complete documentation' }
      ]
    },
    {
      icon: Clock,
      title: { fr: 'Contrats de Maintenance', en: 'Maintenance Contracts' },
      description: { fr: 'Formules adaptées à vos besoins', en: 'Plans tailored to your needs' },
      image: 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740',
      features: [
        { fr: 'Contrats mensuels ou annuels', en: 'Monthly or annual contracts' },
        { fr: 'Tarifs préférentiels', en: 'Preferential rates' },
        { fr: 'Priorité d’intervention', en: 'Intervention priority' },
        { fr: 'Planification flexible', en: 'Flexible scheduling' },
        { fr: 'Suivi personnalisé', en: 'Personalized follow-up' }
      ]
    },
    {
      icon: Wrench,
      title: { fr: 'Maintenance Électrique', en: 'Electrical Maintenance' },
      description: { fr: 'Installations et réparations électriques', en: 'Electrical installations and repairs' },
      image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e',
      features: [
        { fr: 'Installation électrique', en: 'Electrical installation' },
        { fr: 'Réparation tableaux électriques', en: 'Electrical panel repair' },
        { fr: 'Mise aux normes', en: 'Standards compliance' },
        { fr: 'Dépannage électrique', en: 'Electrical troubleshooting' },
        { fr: 'Certifications NF', en: 'NF certifications' }
      ]
    }
  ];

  const industries = [
    { name: { fr: 'Industrie', en: 'Industry' }, icon: Settings },
    { name: { fr: 'Hôtels & Restaurants', en: 'Hotels & Restaurants' }, icon: Hammer },
    { name: { fr: 'Hôpitaux', en: 'Hospitals' }, icon: AlertCircle },
    { name: { fr: 'Bureaux', en: 'Offices' }, icon: Wrench }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1595856898575-9d187bd32fd6)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Wrench className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Maintenance & Réparations' : 'Maintenance & Repairs'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Services de maintenance professionnels pour tous vos équipements' : 'Professional maintenance services for all your equipment'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Services de Maintenance' : 'Our Maintenance Services'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={service.image} alt={service.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{service.description[language]}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{feature[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Secteurs Servis' : 'Sectors Served'}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <Card key={index} className="p-6 text-center bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200 hover:border-blue-500 transition-all">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">{industry.name[language]}</h3>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Intervention Rapide 24/7' : 'Quick 24/7 Intervention'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Notre équipe technique est disponible jour et nuit pour vos urgences' : 'Our technical team is available day and night for your emergencies'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander une Intervention' : 'Request Intervention'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default MaintenanceServices;